import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Layout from '../components/Layout';
import AutoComplete from '../components/AutoComplete';
import DataTable from '../components/DataTable';
import Alert from '../components/Alert';
import { useAuth } from '../context/AuthContext';
import {
  guiaRemisionService, almacenService, trabajadorService,
  entidadService, tiposMovimientoService,
  serieService, tablaService,
} from '../services/api';
import { getErrorDetail } from '../utils/errorHelper';
import '../styles/requerimientos.css';
import '../styles/ordenes_compra.css';
import '../styles/datatable.css';

const today   = () => new Date().toISOString().split('T')[0];
const weekAgo = () => { const d = new Date(); d.setDate(d.getDate() - 6); return d.toISOString().split('T')[0]; };
const fmtN    = (v, d = 4) => (parseFloat(v) || 0).toLocaleString('es-PE', { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtPE   = (n) => `PE-${String(n || 0).padStart(6, '0')}`;

const ESTADO_CFG = {
  'PENDIENTE':    { color: '#60a5fa', bg: 'rgba(96,165,250,0.12)',  border: 'rgba(96,165,250,0.3)'  },
  'ATENDIDO PAR.':{ color: '#fb923c', bg: 'rgba(251,146,60,0.12)',  border: 'rgba(251,146,60,0.3)'  },
  'GENERADO':     { color: '#22c55e', bg: 'rgba(34,197,94,0.12)',   border: 'rgba(34,197,94,0.3)'   },
  'ANULADO':      { color: '#94a3b8', bg: 'rgba(148,163,184,0.1)',  border: 'rgba(148,163,184,0.25)'},
};

const Badge = ({ label, cfg }) => {
  const c = cfg || ESTADO_CFG[label] || { color: '#94a3b8', bg: 'rgba(148,163,184,0.1)', border: 'rgba(148,163,184,0.2)' };
  return (
    <span style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}`, borderRadius: 10, padding: '2px 9px', fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
};

const inputStyle = {
  height: 30, padding: '0 9px', fontSize: 12, borderRadius: 7, boxSizing: 'border-box',
  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)',
  color: '#e2e8f0', outline: 'none', fontFamily: 'Inter, sans-serif', width: '100%',
};
const selectStyle = {
  height: 30, padding: '0 8px', fontSize: 12, borderRadius: 7, boxSizing: 'border-box',
  background: 'rgba(15,27,45,0.9)', border: '1px solid rgba(255,255,255,0.11)',
  color: '#e2e8f0', outline: 'none', fontFamily: 'Inter, sans-serif', width: '100%', cursor: 'pointer',
};
const labelStyle = {
  fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase',
  letterSpacing: '0.6px', display: 'block', marginBottom: 4,
};

export default function GrDesdePe() {
  const { user, sucursal: sesionSucursal } = useAuth();
  const [view, setView] = useState('list'); // 'list' | 'form'

  /* ── Lista de PEs ── */
  const [pesLista,    setPesLista]    = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [alert,       setAlert]       = useState(null);

  /* ── PEs seleccionadas (múltiple) ── */
  const [pesSeleccionadas, setPesSeleccionadas] = useState([]); // array de objetos PE

  /* ── Filtros ── */
  const [fAlmacen,   setFAlmacen]   = useState('');
  const [fNumero,    setFNumero]    = useState('');
  const [fProveedor, setFProveedor] = useState('');
  const [fFechaIni,  setFFechaIni]  = useState(weekAgo);
  const [fFechaFin,  setFFechaFin]  = useState(today);

  /* ── Catálogos ── */
  const [almacenes,       setAlmacenes]       = useState([]);
  const [series,          setSeries]          = useState([]);
  const [trabajadores,    setTrabajadores]    = useState([]);
  const [motivoTraslado,  setMotivoTraslado]  = useState([]);
  const [tiposMovimiento, setTiposMovimiento] = useState([]);
  const [entidades,       setEntidades]       = useState([]);
  const [tiposDoc,        setTiposDoc]        = useState([]);
  const [loadingCat,      setLoadingCat]      = useState(false);
  const catLoaded = useRef(false);

  /* ── Ítems combinados ── */
  const [peItems,       setPeItems]       = useState([]); // items de todos los PEs seleccionados
  const [loadingItems,  setLoadingItems]  = useState(false);
  const [seleccionados, setSeleccionados] = useState({}); // { id_detalle: cantidad }
  const [soloPendientes,setSoloPendientes]= useState(true);

  /* ── Formulario GR ── */
  const FORM_VACIO = {
    tipo_guia:          'REMITENTE',
    id_tipo_movimiento: '',
    id_serie:           '',
    fecha_guia:         today(),
    id_proveedor:       '',
    ruc_proveedor:      '',
    id_tipo_doc_ref1:   '',
    num_doc_ref1:       '',
    id_tipo_doc_ref2:   '',
    num_doc_ref2:       '',
    id_almacen_destino: '',
    fecha_traslado:     today(),
    fecha_entrega:      today(),
    id_solicitante:     '',
    id_motivo_traslado: '',
    observacion:        '',
  };
  const [form, setForm] = useState(FORM_VACIO);
  const [saving, setSaving] = useState(false);
  const [creado, setCreado] = useState(null);

  const mtSvc = useRef(tablaService('motivos-traslado'));
  const tdSvc = useRef(tablaService('tipos-documento'));

  /* ── Carga lista PEs — solo la sucursal del usuario ── */
  const fetchPesLista = useCallback(async () => {
    setLoadingList(true);
    try {
      const r = await guiaRemisionService.getPesParaGr(sesionSucursal?.id_sucursal);
      setPesLista(r.data || []);
    } catch {
      setAlert({ type: 'error', message: 'Error al cargar los partes de entrada.' });
    } finally {
      setLoadingList(false);
    }
  }, [sesionSucursal?.id_sucursal]);

  /* ── Carga catálogos ── */
  const fetchCatalogos = useCallback(async () => {
    if (catLoaded.current) return;
    setLoadingCat(true);
    try {
      const [alm, ser, trab, mt, tm, ent, td] = await Promise.all([
        almacenService.getAll(),
        serieService.getAll('GS'),
        trabajadorService.getAll(),
        mtSvc.current.getAll(),
        tiposMovimientoService.getAll(),
        entidadService.getAll(),
        tdSvc.current.getAll(),
      ]);
      setAlmacenes(alm.data || []);
      setSeries(ser.data || []);
      setTrabajadores(trab.data || []);
      setMotivoTraslado(mt.data || []);
      setTiposMovimiento((tm.data || []).filter(t => t.tipo === 'SALIDA'));
      setEntidades(ent.data || []);
      setTiposDoc(td.data || []);
      catLoaded.current = true;
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al cargar catálogos.') });
    } finally {
      setLoadingCat(false);
    }
  }, []);

  useEffect(() => { fetchPesLista(); }, [fetchPesLista]);

  /* ── Filtros lista ── */
  const pesFiltradas = useMemo(() => {
    return pesLista.filter(pe => {
      if (fAlmacen   && pe.almacen_nombre !== fAlmacen) return false;
      if (fNumero    && !String(pe.numero).includes(fNumero)) return false;
      if (fProveedor && !(pe.proveedor_ruc || '').includes(fProveedor) &&
          !(pe.proveedor_nombre || '').toLowerCase().includes(fProveedor.toLowerCase())) return false;
      const fecha = (pe.fecha_operacion || '').slice(0, 10);
      if (fFechaIni && fecha < fFechaIni) return false;
      if (fFechaFin && fecha > fFechaFin) return false;
      return true;
    });
  }, [pesLista, fAlmacen, fNumero, fProveedor, fFechaIni, fFechaFin]);

  const almacenesPes = useMemo(
    () => [...new Set(pesLista.map(o => o.almacen_nombre).filter(Boolean))].sort(),
    [pesLista]
  );

  const peIdsSeleccionados = useMemo(
    () => new Set(pesSeleccionadas.map(p => p.id_parte_entrada)),
    [pesSeleccionadas]
  );

  /* ── Toggle PE en selección ── */
  const togglePe = (pe) => {
    setPesSeleccionadas(prev => {
      const yaEsta = prev.some(p => p.id_parte_entrada === pe.id_parte_entrada);
      return yaEsta
        ? prev.filter(p => p.id_parte_entrada !== pe.id_parte_entrada)
        : [...prev, pe];
    });
  };

  /* ── Ir al formulario ── */
  const irAFormulario = async () => {
    if (pesSeleccionadas.length === 0) {
      setAlert({ type: 'warning', message: 'Seleccione al menos un parte de entrada.' });
      return;
    }
    fetchCatalogos();
    const primerPe = pesSeleccionadas[0];
    setForm({
      ...FORM_VACIO,
      id_proveedor:  pesSeleccionadas.length === 1 ? String(primerPe.id_proveedor || '') : '',
      ruc_proveedor: pesSeleccionadas.length === 1 ? (primerPe.ruc_proveedor || '') : '',
      id_solicitante: primerPe?.id_solicitante ? String(primerPe.id_solicitante) : '',
    });
    setSeleccionados({});
    setPeItems([]);
    setCreado(null);
    setView('form');

    // Cargar ítems de todos los PEs en paralelo
    setLoadingItems(true);
    try {
      const resultados = await Promise.all(
        pesSeleccionadas.map(pe => guiaRemisionService.getPeItems(pe.id_parte_entrada))
      );
      // Combinar ítems de todos los PEs (el backend ya incluye pe_numero en cada ítem)
      const combinados = [];
      resultados.forEach((res) => {
        (res.data || []).forEach(item => combinados.push(item));
      });
      setPeItems(combinados);
      // Pre-seleccionar todos los que tienen pendiente > 0
      const presel = {};
      combinados.forEach(it => {
        const pend = parseFloat(it.cantidad_pendiente) || 0;
        if (pend > 0) presel[it.id_detalle] = pend;
      });
      setSeleccionados(presel);
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al cargar artículos de los partes de entrada.') });
    } finally {
      setLoadingItems(false);
    }
  };

  /* ── Ítems visibles ── */
  const itemsVisibles = useMemo(() => {
    if (soloPendientes) return peItems.filter(it => (parseFloat(it.cantidad_pendiente) || 0) > 0);
    return peItems;
  }, [peItems, soloPendientes]);

  /* ── Selección de ítems ── */
  const toggleItem = (it) => {
    const key = it.id_detalle;
    setSeleccionados(prev => {
      if (key in prev) { const n = { ...prev }; delete n[key]; return n; }
      const pend = parseFloat(it.cantidad_pendiente) || 0;
      return { ...prev, [key]: pend > 0 ? pend : parseFloat(it.cantidad) || 1 };
    });
  };

  const setCantidadItem = (id_detalle, val) =>
    setSeleccionados(prev => ({ ...prev, [id_detalle]: val }));

  const aptosPendientes = useMemo(
    () => itemsVisibles.filter(it => (parseFloat(it.cantidad_pendiente) || 0) > 0),
    [itemsVisibles]
  );
  const todosSeleccionados = aptosPendientes.length > 0 && aptosPendientes.every(it => it.id_detalle in seleccionados);

  const toggleAll = () => {
    if (todosSeleccionados) {
      setSeleccionados(prev => {
        const n = { ...prev };
        aptosPendientes.forEach(it => delete n[it.id_detalle]);
        return n;
      });
    } else {
      const add = {};
      aptosPendientes.forEach(it => {
        if (!(it.id_detalle in seleccionados))
          add[it.id_detalle] = parseFloat(it.cantidad_pendiente) || 1;
      });
      setSeleccionados(prev => ({ ...prev, ...add }));
    }
  };

  const nSel = Object.keys(seleccionados).length;
  const setF  = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const n     = v => (v !== '' && v !== null && v !== undefined) ? Number(v) : null;

  /* ── Guardar GR ── */
  const handleSubmit = async () => {
    if (!form.fecha_guia) { setAlert({ type: 'warning', message: 'Ingrese la fecha de guía.' }); return; }
    if (form.fecha_entrega && form.fecha_guia && form.fecha_entrega < form.fecha_guia) {
      setAlert({ type: 'warning', message: 'La fecha de entrega no puede ser menor a la fecha de la guía.' }); return;
    }
    if (form.fecha_entrega && form.fecha_traslado && form.fecha_entrega < form.fecha_traslado) {
      setAlert({ type: 'warning', message: 'La fecha de entrega no puede ser menor a la fecha de traslado.' }); return;
    }
    const itemsSel = peItems.filter(it => it.id_detalle in seleccionados);
    if (itemsSel.length === 0) { setAlert({ type: 'warning', message: 'Seleccione al menos un artículo.' }); return; }
    for (const it of itemsSel) {
      const qty      = parseFloat(seleccionados[it.id_detalle]) || 0;
      const pendiente = parseFloat(it.cantidad_pendiente) || 0;
      if (qty <= 0)        { setAlert({ type: 'warning', message: `Cantidad de "${it.articulo_descripcion}" debe ser mayor a 0.` }); return; }
      if (qty > pendiente) { setAlert({ type: 'warning', message: `Cantidad de "${it.articulo_descripcion}" excede la pendiente (${fmtN(pendiente, 4)}).` }); return; }
    }

    // Usar el almacén y sucursal del primer PE seleccionado
    const primerPe = pesSeleccionadas[0];
    const payload = {
      tipo_guia:          form.tipo_guia,
      fecha_guia:         form.fecha_guia,
      id_almacen:         primerPe.id_almacen,
      id_sucursal:        primerPe.id_sucursal || (sesionSucursal?.id_sucursal ? n(sesionSucursal.id_sucursal) : null),
      id_tipo_movimiento: n(form.id_tipo_movimiento),
      id_serie:           n(form.id_serie),
      id_proveedor:       n(form.id_proveedor) || null,
      ruc_proveedor:      form.ruc_proveedor || null,
      id_tipo_doc_ref1:   n(form.id_tipo_doc_ref1),
      num_doc_ref1:       form.num_doc_ref1 || null,
      id_tipo_doc_ref2:   n(form.id_tipo_doc_ref2),
      num_doc_ref2:       form.num_doc_ref2 || null,
      id_almacen_destino: n(form.id_almacen_destino),
      fecha_traslado:     form.fecha_traslado || null,
      fecha_entrega:      form.fecha_entrega  || null,
      id_solicitante:     n(form.id_solicitante),
      id_motivo_traslado: n(form.id_motivo_traslado),
      observacion:        form.observacion || null,
      id_parte_entrada:   pesSeleccionadas.length === 1 ? primerPe.id_parte_entrada : null,
      detalle: itemsSel.map((it, i) => ({
        orden:         i + 1,
        id_articulo:   it.id_articulo,
        id_unidad:     it.id_unidad || null,
        cantidad:      parseFloat(seleccionados[it.id_detalle]) || 1,
        id_detalle_pe: it.id_detalle,
      })),
    };

    setSaving(true);
    try {
      const res = await guiaRemisionService.create(payload);
      setCreado(res.data);
      fetchPesLista();
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al generar la guía de remisión.') });
    } finally {
      setSaving(false);
    }
  };

  /* ════════════════════════════════════════════════════════════════
     COLUMNAS LISTA PEs
  ════════════════════════════════════════════════════════════════ */
  const tableColumns = [
    {
      key: '_sel', label: '', width: 48,
      render: r => {
        const sel = peIdsSeleccionados.has(r.id_parte_entrada);
        return (
          <button
            onClick={() => togglePe(r)}
            title={sel ? 'Quitar' : 'Agregar a selección'}
            style={{
              width: 28, height: 28, borderRadius: 6, border: `1px solid ${sel ? 'rgba(239,68,68,0.4)' : 'rgba(96,165,250,0.35)'}`,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: sel ? 'rgba(239,68,68,0.1)' : 'rgba(96,165,250,0.1)',
              color: sel ? '#f87171' : '#60a5fa', fontSize: 16, fontWeight: 700,
              transition: 'all 0.15s',
            }}
          >
            {sel ? '−' : '+'}
          </button>
        );
      },
    },
    {
      key: 'numero', label: 'Número PE', width: 100,
      render: r => (
        <span style={{ fontWeight: 700, color: '#60a5fa', fontSize: 12, fontFamily: 'monospace' }}>
          {fmtPE(r.numero)}
        </span>
      ),
    },
    {
      key: 'fecha_operacion', label: 'Fecha', width: 88,
      render: r => <span style={{ fontSize: 11 }}>{(r.fecha_operacion || '').slice(0, 10) || '—'}</span>,
    },
    { key: 'almacen_nombre', label: 'Almacén', style: { fontSize: 11 } },
    {
      key: 'proveedor_nombre', label: 'Proveedor',
      render: r => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          {r.ruc_proveedor && <span style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace', flexShrink: 0 }}>{r.ruc_proveedor}</span>}
          {r.ruc_proveedor && r.proveedor_nombre && <span style={{ color: '#374151' }}>—</span>}
          <span style={{ fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.proveedor_nombre || '—'}</span>
        </div>
      ),
    },
    {
      key: 'estado_gr', label: 'Atención GR', width: 115,
      render: r => <Badge label={r.estado_gr || 'PENDIENTE'} />,
    },
    {
      key: 'total_grs', label: 'GRs', width: 55,
      render: r => r.total_grs > 0
        ? <span style={{ background: 'rgba(167,139,250,0.12)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.3)', borderRadius: 10, padding: '2px 8px', fontSize: 10.5, fontWeight: 700 }}>{r.total_grs}</span>
        : <span style={{ color: '#374151', fontSize: 11 }}>—</span>,
    },
  ];

  /* ════════════════════════════════════════════════════════════════
     PANTALLA ÉXITO
  ════════════════════════════════════════════════════════════════ */
  if (creado) {
    const ec = ESTADO_CFG[creado.estado] || {};
    const numGR = `${(creado.serie_codigo || '').trim()}-${String(creado.numero || 0).padStart(6, '0')}`;
    return (
      <Layout>
        <div style={{ maxWidth: 520, margin: '80px auto', textAlign: 'center', padding: '0 20px' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(34,197,94,0.1)', border: '2px solid rgba(34,197,94,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div style={{ fontSize: 13, color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600, marginBottom: 8 }}>
            Guía de Remisión generada
          </div>
          <div style={{ fontSize: 32, fontWeight: 800, color: '#60a5fa', letterSpacing: 2, marginBottom: 6 }}>
            {numGR}
          </div>
          <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 4 }}>
            {creado.almacen_nombre} · {(creado.fecha_guia || '').slice(0, 10)}
          </div>
          <div style={{ fontSize: 11, color: '#475569', marginBottom: 6 }}>
            desde {pesSeleccionadas.map(p => fmtPE(p.numero)).join(', ')}
          </div>
          <Badge label={creado.estado || 'GENERADO'} cfg={ec} />
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 36 }}>
            <button className="btn btn-secondary" onClick={() => { setCreado(null); setPesSeleccionadas([]); setView('list'); }}>
              Ver lista de PEs
            </button>
            <button className="btn btn-primary" onClick={() => { setCreado(null); irAFormulario(); }}>
              + Nueva GR con mismos PEs
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  /* ════════════════════════════════════════════════════════════════
     VISTA LISTA
  ════════════════════════════════════════════════════════════════ */
  if (view === 'list') {
    return (
      <Layout>
        {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

          {/* Header */}
          <div className="oc-page-header" style={{ flexShrink: 0 }}>
            <h2>GR desde Parte de Entrada</h2>
            <div className="oc-header-actions">
              <button className="btn btn-secondary btn-sm" onClick={fetchPesLista}>↺ Actualizar</button>
            </div>
          </div>

          {/* Filtros */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1.2fr 1fr 1.4fr 1fr 1fr auto',
            gap: 10, marginBottom: 10, flexShrink: 0,
            background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: 10, padding: '12px 16px',
          }}>
            <div>
              <label style={labelStyle}>Almacén</label>
              <select value={fAlmacen} onChange={e => setFAlmacen(e.target.value)} style={selectStyle}>
                <option value="">Todos</option>
                {almacenesPes.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Número PE</label>
              <input value={fNumero} onChange={e => setFNumero(e.target.value)} placeholder="Buscar..." style={inputStyle}
                onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.09)'} />
            </div>
            <div>
              <label style={labelStyle}>Proveedor</label>
              <input value={fProveedor} onChange={e => setFProveedor(e.target.value)} placeholder="RUC o razón social..." style={inputStyle}
                onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.09)'} />
            </div>
            <div>
              <label style={labelStyle}>Fecha desde</label>
              <input type="date" value={fFechaIni} onChange={e => setFFechaIni(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Fecha hasta</label>
              <input type="date" value={fFechaFin} onChange={e => setFFechaFin(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button
                onClick={() => { setFAlmacen(''); setFNumero(''); setFProveedor(''); setFFechaIni(weekAgo()); setFFechaFin(today()); }}
                style={{ height: 30, padding: '0 12px', fontSize: 11, fontWeight: 600, borderRadius: 7, border: '1px solid rgba(255,255,255,0.07)', cursor: 'pointer', background: 'rgba(255,255,255,0.03)', color: '#64748b', fontFamily: 'Inter, sans-serif' }}
              >✕ Limpiar</button>
            </div>
          </div>

          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6, paddingLeft: 2, flexShrink: 0 }}>
            {pesFiltradas.length} resultado{pesFiltradas.length !== 1 ? 's' : ''}
            {' '}· Solo PEs con cantidad pendiente de despacho
          </div>

          <div style={{ flex: 1, overflow: 'hidden' }}>
            <DataTable columns={tableColumns} data={pesFiltradas} loading={loadingList} hideSearch hideToolbarRight />
          </div>

          {/* ── Panel inferior: PEs seleccionados ── */}
          {pesSeleccionadas.length > 0 && (
            <div style={{
              flexShrink: 0, marginTop: 8, padding: '12px 16px',
              background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.25)',
              borderRadius: 10, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            }}>
              <span style={{ fontSize: 11, color: '#60a5fa', fontWeight: 700, whiteSpace: 'nowrap' }}>
                PEs seleccionados ({pesSeleccionadas.length}):
              </span>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1 }}>
                {pesSeleccionadas.map(pe => (
                  <div key={pe.id_parte_entrada} style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.3)',
                    borderRadius: 8, padding: '3px 8px',
                  }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#60a5fa', fontFamily: 'monospace' }}>
                      {fmtPE(pe.numero)}
                    </span>
                    {pe.almacen_nombre && (
                      <span style={{ fontSize: 10, color: '#94a3b8' }}>{pe.almacen_nombre}</span>
                    )}
                    <button
                      onClick={() => togglePe(pe)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f87171', fontSize: 14, lineHeight: 1, padding: '0 2px' }}
                    >×</button>
                  </div>
                ))}
              </div>
              <button
                className="btn btn-primary"
                onClick={irAFormulario}
                style={{ whiteSpace: 'nowrap', flexShrink: 0 }}
              >
                Crear GR con {pesSeleccionadas.length} PE{pesSeleccionadas.length !== 1 ? 's' : ''} →
              </button>
            </div>
          )}
        </div>
      </Layout>
    );
  }

  /* ════════════════════════════════════════════════════════════════
     VISTA FORMULARIO GR
  ════════════════════════════════════════════════════════════════ */
  const almacenesDestino = almacenes.map(a => ({
    ...a,
    display_destino: `${a.nombre}${a.direccion ? ' | ' + a.direccion : ''}${a.ubigeo ? ' [' + a.ubigeo + ']' : ''}`,
  }));

  return (
    <Layout>
      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      <div className="rq-page">
        {/* ── Header ── */}
        <div className="rq-header">
          <div className="rq-header-left">
            <button className="oc-back-btn" onClick={() => setView('list')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <div className="rq-header-title">
              <div className="rq-header-meta">Almacén — GR desde PE</div>
              <div className="rq-header-name">
                {pesSeleccionadas.map(p => fmtPE(p.numero)).join(', ')}
              </div>
            </div>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="rq-body" style={{ overflowY: 'auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>

            {/* ── SECCIÓN 1: PEs origen ── */}
            <div className="rq-card">
              <div className="rq-card-title">Partes de Entrada origen</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {pesSeleccionadas.map(pe => (
                  <div key={pe.id_parte_entrada} style={{
                    background: 'rgba(96,165,250,0.07)', border: '1px solid rgba(96,165,250,0.2)',
                    borderRadius: 9, padding: '8px 14px', minWidth: 200,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#60a5fa', fontSize: 13 }}>
                        {fmtPE(pe.numero)}
                      </span>
                      <Badge label={pe.estado_gr || 'PENDIENTE'} />
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>
                      {pe.almacen_nombre} · {(pe.fecha_operacion || '').slice(0, 10)}
                    </div>
                    {pe.proveedor_nombre && (
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                        {pe.ruc_proveedor && <span style={{ fontFamily: 'monospace', marginRight: 4 }}>{pe.ruc_proveedor}</span>}
                        {pe.proveedor_nombre}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* ── SECCIÓN 2: Identificación ── */}
            <div className="rq-card">
              <div className="rq-card-title">Identificación</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 80px 1fr', gap: '0 14px', alignItems: 'start' }}>
                {/* Sucursal — solo lectura */}
                <div className="form-row">
                  <label>Sucursal</label>
                  <div style={{ padding: '7px 12px', borderRadius: 8, fontSize: 13, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#94a3b8', minHeight: 34, display: 'flex', alignItems: 'center' }}>
                    {sesionSucursal?.nombre || pesSeleccionadas[0]?.sucursal_nombre || '—'}
                  </div>
                </div>
                {/* Almacén — solo lectura desde el PE */}
                <div className="form-row">
                  <label>Almacén</label>
                  <div style={{ padding: '7px 12px', borderRadius: 8, fontSize: 13, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#94a3b8', minHeight: 34, display: 'flex', alignItems: 'center' }}>
                    {pesSeleccionadas[0]?.almacen_nombre || '—'}
                  </div>
                </div>
                <AutoComplete
                  label="Tipo de Movimiento"
                  value={String(form.id_tipo_movimiento || '')}
                  options={tiposMovimiento} idField="id_tipo_movimiento"
                  displayField={t => `${t.codigo} - ${t.descripcion}`}
                  onChange={v => setF('id_tipo_movimiento', v)}
                  placeholder="Seleccionar tipo (SALIDA)..."
                />
                {/* Tipo Doc — solo lectura */}
                <div className="form-row">
                  <label>Tipo Doc.</label>
                  <div style={{ padding: '7px 10px', borderRadius: 8, fontSize: 13, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#94a3b8', minHeight: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, letterSpacing: 1 }}>
                    GS
                  </div>
                </div>
                <AutoComplete
                  label="Serie (GS)"
                  value={String(form.id_serie || '')}
                  options={series} idField="id_serie"
                  displayField={s => `${s.serie} - ${s.descripcion || ''}`}
                  onChange={v => setF('id_serie', v)}
                  placeholder="Seleccionar serie..."
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: '0 14px', alignItems: 'start', marginTop: 4 }}>
                <div className="form-row">
                  <label>Tipo de Guía *</label>
                  <select value={form.tipo_guia} onChange={e => setF('tipo_guia', e.target.value)} style={selectStyle}>
                    <option value="REMITENTE">REMITENTE</option>
                    <option value="TRANSPORTISTA">TRANSPORTISTA</option>
                  </select>
                </div>
                <div className="form-row">
                  <label>Fecha de Guía *</label>
                  <input type="date" value={form.fecha_guia} onChange={e => setF('fecha_guia', e.target.value)} style={inputStyle} />
                </div>
                <AutoComplete
                  label="Proveedor / Cliente"
                  value={String(form.id_proveedor || '')}
                  options={entidades} idField="id_entidad"
                  displayField={e => `${e.ruc || ''} — ${e.razon_social}`}
                  onChange={v => {
                    const ent = entidades.find(x => String(x.id_entidad) === String(v));
                    setForm(f => ({ ...f, id_proveedor: v || '', ruc_proveedor: ent?.ruc || '' }));
                  }}
                  placeholder="Buscar por RUC o razón social..."
                />
              </div>
            </div>

            {/* ── SECCIÓN 3: Referencias Documentales ── */}
            <div className="rq-card">
              <div className="rq-card-title">Referencias Documentales</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0 14px', alignItems: 'start' }}>
                <AutoComplete
                  label="Doc. Referencia 1"
                  value={String(form.id_tipo_doc_ref1 || '')}
                  options={tiposDoc} idField="id_tipo_documento" displayField="descripcion"
                  onChange={v => setF('id_tipo_doc_ref1', v)}
                  placeholder="Tipo documento..."
                />
                <div className="form-row">
                  <label>Nº Ref. 1</label>
                  <input value={form.num_doc_ref1} onChange={e => setF('num_doc_ref1', e.target.value)} placeholder="Número..." style={inputStyle}
                    onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.09)'} />
                </div>
                <AutoComplete
                  label="Doc. Referencia 2"
                  value={String(form.id_tipo_doc_ref2 || '')}
                  options={tiposDoc} idField="id_tipo_documento" displayField="descripcion"
                  onChange={v => setF('id_tipo_doc_ref2', v)}
                  placeholder="Tipo documento..."
                />
                <div className="form-row">
                  <label>Nº Ref. 2</label>
                  <input value={form.num_doc_ref2} onChange={e => setF('num_doc_ref2', e.target.value)} placeholder="Número..." style={inputStyle}
                    onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.09)'} />
                </div>
              </div>
            </div>

            {/* ── SECCIÓN 4: Datos de Traslado ── */}
            <div className="rq-card">
              <div className="rq-card-title">Datos de Traslado</div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '0 14px', alignItems: 'start' }}>
                <AutoComplete
                  label="Lugar de Entrega (Almacén destino)"
                  value={String(form.id_almacen_destino || '')}
                  options={almacenesDestino} idField="id_almacen" displayField="display_destino"
                  onChange={v => setF('id_almacen_destino', v)}
                  placeholder="Buscar almacén destino..."
                  dropdownMinWidth={420}
                />
                <div className="form-row">
                  <label>Fecha de Traslado</label>
                  <input type="date" value={form.fecha_traslado} onChange={e => setF('fecha_traslado', e.target.value)} style={inputStyle} />
                </div>
                <div className="form-row">
                  <label>Fecha de Entrega</label>
                  <input type="date" value={form.fecha_entrega}
                    min={form.fecha_guia > form.fecha_traslado ? form.fecha_guia : form.fecha_traslado}
                    onChange={e => setF('fecha_entrega', e.target.value)} style={inputStyle} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: '0 14px', alignItems: 'start', marginTop: 4 }}>
                <AutoComplete
                  label="Solicitante"
                  value={String(form.id_solicitante || '')}
                  options={trabajadores} idField="id_trabajador" displayField="nombres"
                  onChange={v => setF('id_solicitante', v)}
                  placeholder="Buscar trabajador..."
                  twoLine codeField="dni"
                />
                <AutoComplete
                  label="Motivo de Traslado"
                  value={String(form.id_motivo_traslado || '')}
                  options={motivoTraslado} idField="id_motivo_traslado"
                  displayField={m => `${m.codigo} - ${m.descripcion}`}
                  onChange={v => setF('id_motivo_traslado', v)}
                  placeholder="Seleccionar motivo..."
                />
                <div className="form-row" style={{ display: 'flex', flexDirection: 'column' }}>
                  <label>Observaciones</label>
                  <textarea value={form.observacion} onChange={e => setF('observacion', e.target.value)}
                    rows={2} placeholder="Observaciones de la guía..."
                    style={{ flex: 1, width: '100%', boxSizing: 'border-box', padding: '6px 10px', fontSize: 12.5, borderRadius: 8, resize: 'vertical', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.11)', color: '#e2e8f0', fontFamily: 'Inter, sans-serif', outline: 'none', minHeight: 30 }}
                    onFocus={e => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.15)'; }}
                    onBlur={e  => { e.target.style.borderColor = 'rgba(255,255,255,0.11)'; e.target.style.boxShadow = 'none'; }}
                  />
                </div>
              </div>
            </div>

            {/* ── SECCIÓN 3: Ítems ── */}
            <div className="rq-card" style={{ flex: 'none' }}>
              <div className="rq-table-header">
                <div className="rq-table-header-left">
                  <span className="rq-table-title">Artículos</span>
                  <div style={{ display: 'flex', gap: 4, marginLeft: 8 }}>
                    <button onClick={() => setSoloPendientes(false)} style={{ fontSize: 11, padding: '2px 9px', borderRadius: 5, border: '1px solid', cursor: 'pointer', fontWeight: 600, transition: 'all 0.15s', background: !soloPendientes ? 'rgba(96,165,250,0.15)' : 'rgba(255,255,255,0.04)', color: !soloPendientes ? '#60a5fa' : '#64748b', borderColor: !soloPendientes ? 'rgba(96,165,250,0.4)' : 'rgba(255,255,255,0.1)' }}>Todos</button>
                    <button onClick={() => setSoloPendientes(true)}  style={{ fontSize: 11, padding: '2px 9px', borderRadius: 5, border: '1px solid', cursor: 'pointer', fontWeight: 600, transition: 'all 0.15s', background:  soloPendientes ? 'rgba(96,165,250,0.15)' : 'rgba(255,255,255,0.04)', color:  soloPendientes ? '#60a5fa' : '#64748b', borderColor:  soloPendientes ? 'rgba(96,165,250,0.4)' : 'rgba(255,255,255,0.1)' }}>Solo pendientes</button>
                  </div>
                </div>
                <span style={{ fontSize: 11, color: '#475569' }}>
                  {nSel > 0 ? `${nSel} ítem${nSel !== 1 ? 's' : ''} marcado${nSel !== 1 ? 's' : ''}` : 'Sin selección'}
                </span>
              </div>

              {loadingItems && <div style={{ textAlign: 'center', padding: 28, color: '#64748b', fontSize: 13 }}>Cargando artículos...</div>}

              {!loadingItems && (
                <div style={{ border: '1px solid rgba(255,255,255,0.07)', borderRadius: 9, overflowX: 'auto', background: 'rgba(0,0,0,0.12)', minHeight: 80 }}>
                  <table className="rq-articles" style={{ minWidth: 1080 }}>
                    <thead>
                      <tr>
                        <th style={{ width: 36, textAlign: 'center' }}>
                          <input type="checkbox" checked={todosSeleccionados} onChange={toggleAll}
                            title="Seleccionar todos los pendientes"
                            style={{ cursor: 'pointer', accentColor: '#3b82f6', width: 13, height: 13 }} />
                        </th>
                        <th style={{ width: 32 }}>#</th>
                        <th style={{ width: 90, textAlign: 'center' }}>PE</th>
                        <th style={{ width: 108 }}>Código</th>
                        <th>Artículo</th>
                        <th style={{ width: 60, textAlign: 'center' }}>U.M.</th>
                        <th style={{ width: 90, textAlign: 'center' }}>OC</th>
                        <th style={{ width: 90, textAlign: 'center' }}>RQ</th>
                        <th style={{ width: 86, textAlign: 'right' }}>Cant. PE</th>
                        <th style={{ width: 86, textAlign: 'right' }}>GR Emitida</th>
                        <th style={{ width: 80, textAlign: 'right' }}>Pendiente</th>
                        <th style={{ width: 105, textAlign: 'right' }}>A despachar ✎</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itemsVisibles.length === 0 && (
                        <tr><td colSpan={12} className="rq-empty">
                          {soloPendientes ? 'No hay artículos pendientes de despacho.' : 'Los partes de entrada seleccionados no tienen artículos.'}
                        </td></tr>
                      )}
                      {itemsVisibles.map((it, i) => {
                        const pendiente     = parseFloat(it.cantidad_pendiente) || 0;
                        const grEmitida     = parseFloat(it.cantidad_gr)        || 0;
                        const totalPe       = parseFloat(it.cantidad)           || 0;
                        const agotado       = pendiente <= 0;
                        const checked       = it.id_detalle in seleccionados;
                        const cantDespachar = checked ? (parseFloat(seleccionados[it.id_detalle]) || 0) : 0;
                        const ocLabel = it.oc_numero || null;
                        const rqLabel = it.rq_numero || null;

                        return (
                          <tr key={it.id_detalle} style={{ opacity: agotado ? 0.4 : 1, background: checked ? 'rgba(59,130,246,0.07)' : 'transparent' }}>
                            <td style={{ textAlign: 'center', padding: '4px 6px' }}>
                              {agotado
                                ? <span title="Completamente despachado" style={{ fontSize: 14, color: '#22c55e' }}>✓</span>
                                : <input type="checkbox" checked={checked} onChange={() => toggleItem(it)} style={{ cursor: 'pointer', accentColor: '#3b82f6', width: 14, height: 14 }} />}
                            </td>
                            <td className="rq-td-num">{i + 1}</td>
                            <td style={{ textAlign: 'center', padding: '4px 6px' }}>
                              <span style={{ fontSize: 10.5, fontWeight: 700, color: '#60a5fa', background: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.25)', borderRadius: 5, padding: '2px 6px', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
                                {fmtPE(it.pe_numero)}
                              </span>
                            </td>
                            <td style={{ padding: '4px 8px', color: '#94a3b8', fontFamily: 'monospace', fontSize: 10.5, whiteSpace: 'nowrap' }}>
                              {it.articulo_codigo || '—'}
                            </td>
                            <td style={{ padding: '4px 8px', minWidth: 160 }}>
                              <div style={{ fontSize: 12, color: '#e2e8f0', fontWeight: 500 }}>{it.articulo_descripcion || '—'}</div>
                            </td>
                            <td style={{ textAlign: 'center', padding: '4px 6px', color: '#64748b', fontSize: 11 }}>{it.unidad_codigo || '—'}</td>
                            <td style={{ textAlign: 'center', padding: '4px 6px' }}>
                              {ocLabel
                                ? <span style={{ fontSize: 10.5, fontWeight: 700, color: '#a78bfa', background: 'rgba(167,139,250,0.1)', border: '1px solid rgba(167,139,250,0.25)', borderRadius: 5, padding: '2px 6px', whiteSpace: 'nowrap' }}>{ocLabel}</span>
                                : <span style={{ color: '#374151', fontSize: 10 }}>—</span>}
                            </td>
                            <td style={{ textAlign: 'center', padding: '4px 6px' }}>
                              {rqLabel
                                ? <span style={{ fontSize: 10.5, fontWeight: 700, color: '#fb923c', background: 'rgba(251,146,60,0.1)', border: '1px solid rgba(251,146,60,0.25)', borderRadius: 5, padding: '2px 6px', whiteSpace: 'nowrap' }}>{rqLabel}</span>
                                : <span style={{ color: '#374151', fontSize: 10 }}>—</span>}
                            </td>
                            <td style={{ textAlign: 'right', padding: '4px 8px', color: '#cbd5e1', fontVariantNumeric: 'tabular-nums', fontSize: 11.5 }}>{fmtN(totalPe)}</td>
                            <td style={{ textAlign: 'right', padding: '4px 8px', color: grEmitida > 0 ? '#a78bfa' : '#475569', fontVariantNumeric: 'tabular-nums', fontSize: 11.5 }}>{fmtN(grEmitida)}</td>
                            <td style={{ textAlign: 'right', padding: '4px 8px', color: agotado ? '#22c55e' : '#e2e8f0', fontWeight: 600, fontVariantNumeric: 'tabular-nums', fontSize: 11.5 }}>{fmtN(pendiente)}</td>
                            <td style={{ padding: '3px 6px' }}>
                              {checked ? (
                                <input
                                  type="number" min="0.0001" step="0.0001" max={pendiente}
                                  value={cantDespachar}
                                  onChange={e => setCantidadItem(it.id_detalle, parseFloat(e.target.value) || 0)}
                                  style={{
                                    width: '100%', height: 28, padding: '0 7px', boxSizing: 'border-box',
                                    textAlign: 'right', fontSize: 12, fontFamily: 'Inter, sans-serif',
                                    background: cantDespachar > pendiente ? 'rgba(239,68,68,0.12)' : 'rgba(59,130,246,0.08)',
                                    border: `1px solid ${cantDespachar > pendiente ? 'rgba(239,68,68,0.4)' : 'rgba(59,130,246,0.3)'}`,
                                    borderRadius: 6, color: '#e2e8f0', outline: 'none',
                                  }}
                                  onFocus={e => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 2px rgba(59,130,246,0.15)'; }}
                                  onBlur={e  => { e.target.style.borderColor = cantDespachar > pendiente ? 'rgba(239,68,68,0.4)' : 'rgba(59,130,246,0.3)'; e.target.style.boxShadow = 'none'; }}
                                />
                              ) : (
                                <div style={{ textAlign: 'right', padding: '0 7px', color: '#374151', fontSize: 12 }}>—</div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>
        </div>

        {/* ── Footer ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.07)', background: 'rgba(0,0,0,0.15)', flexShrink: 0 }}>
          <button className="btn btn-secondary" onClick={() => setView('list')} disabled={saving}>
            ← Volver a lista
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {nSel > 0 && (
              <span style={{ fontSize: 12, color: '#64748b', fontFamily: 'Inter, sans-serif' }}>
                {nSel} artículo{nSel !== 1 ? 's' : ''} seleccionado{nSel !== 1 ? 's' : ''}
              </span>
            )}
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={saving || loadingCat || nSel === 0}
            >
              {saving ? 'Guardando...' : 'Generar GR'}
            </button>
          </div>
        </div>

      </div>
    </Layout>
  );
}
